// ── Workflow Journey View ─────────────────────────────────────
// Readable document view with node navigation.
// Left rail: "All Output" (full doc) + every tree node as a TOC entry.
// Main panel: full synthesized output or per-node detail.
// Prominent toggle to switch back to graph view.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getNodeConfig } from './nodeConfig';
import ContractChecklist from './ContractChecklist';

// ── Node type badge ──────────────────────────────────────────
function TypeBadge({ type }) {
  const config = getNodeConfig(type);
  return (
    <span
      className="wj-type-badge"
      style={{
        color: config.color,
        borderColor: config.color + '44',
        background: config.color + '12',
      }}
    >
      {config.icon} {config.label}
    </span>
  );
}

// ── Elapsed timer ────────────────────────────────────────────
function ElapsedTimer({ startedAt }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return (
    <span className="wj-elapsed">
      {mins > 0 ? `${mins}m ${secs}s` : `${secs}s`}
    </span>
  );
}

// ── Stage progress item ─────────────────────────────────────
function StageItem({ stage }) {
  const statusIcon = stage.status === 'done' ? '✓'
    : stage.status === 'active' ? '●'
    : '○';
  const statusClass = `wj-progress-stage wj-stage-${stage.status}`;

  return (
    <div className={statusClass}>
      <span className="wj-progress-icon">{statusIcon}</span>
      <span className="wj-progress-label">{stage.label}</span>
      {stage.nodeCount > 0 && (
        <span className="wj-progress-nodes">{stage.nodeCount} nodes</span>
      )}
      {stage.detail && stage.status === 'active' && (
        <span className="wj-progress-detail">{stage.detail}</span>
      )}
    </div>
  );
}

// ── Section actions (per-heading copy + deep-dive) ──────────
// Renders next to each h2/h3 in the synthesized doc. Reads the heading's
// rendered text + the next sibling paragraph from the live DOM at click
// time so it always reflects what the user sees.
function SectionActions({ heading, drill, copyOne }) {
  const collectSection = (e) => {
    // Walk up to the h2/h3 node, then grab text from heading + next siblings
    // until the following heading of equal-or-higher level.
    const headEl = e.currentTarget.closest('h2, h3');
    if (!headEl) return { title: '', body: '' };
    const headLevel = parseInt(headEl.tagName.slice(1), 10);
    const title = headEl.innerText.replace(/[🔬⧉✓\s]+$/, '').trim();
    const parts = [];
    let n = headEl.nextElementSibling;
    while (n) {
      if (/^H[1-6]$/.test(n.tagName)) {
        const lvl = parseInt(n.tagName.slice(1), 10);
        if (lvl <= headLevel) break;
      }
      parts.push(n.innerText || '');
      n = n.nextElementSibling;
    }
    return { title, body: parts.join('\n\n').trim() };
  };
  const onDrill = (e) => {
    e.stopPropagation();
    const { title, body } = collectSection(e);
    if (title) drill(title, body, 'output section');
  };
  const onCopy = async (e) => {
    e.stopPropagation();
    const { title, body } = collectSection(e);
    try { await navigator.clipboard.writeText(`${title}\n\n${body}`); } catch {}
  };
  return (
    <span
      style={{ display: 'inline-flex', gap: 4, opacity: 0.6 }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
    >
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy this section"
        title="Copy this section"
        style={{ background: 'transparent', border: 'none', color: 'var(--text-muted, #888)', cursor: 'pointer', fontSize: 12, padding: '2px 4px' }}
      >⧉</button>
      <button
        type="button"
        onClick={onDrill}
        aria-label="Deep-dive this section"
        title="Drill into this section using the novelty engine"
        style={{ background: 'transparent', border: 'none', color: 'var(--accent, #a78bfa)', cursor: 'pointer', fontSize: 12, padding: '2px 4px' }}
      >🔬</button>
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function WorkflowJourneyView({
  patternName,
  stageHistory,
  isExecuting,
  isSynthesizing,
  currentStage,
  startedAt,
  displayNodes,
  synthesizedOutput,
  onToggleGraphView,
  onDrillNovelty,
  contractVerification,
}) {
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied'
  const [selectedText, setSelectedText] = useState('');

  // Track text selection inside the All Output doc so we can offer a
  // contextual deep-dive button. Polled on selectionchange — cheap, fires
  // only while the user is actively highlighting.
  useEffect(() => {
    const handler = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() || '';
      // Only count selections inside the output doc, not the rail or sidebar.
      const anchor = sel?.anchorNode?.parentElement?.closest?.('.wj-output-doc');
      setSelectedText(anchor && text.length >= 8 ? text : '');
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  const copyOutput = async () => {
    if (!synthesizedOutput) return;
    try {
      await navigator.clipboard.writeText(synthesizedOutput);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('idle');
    }
  };

  const drillSelection = () => {
    if (!onDrillNovelty || !selectedText) return;
    // First sentence (or first 120 chars) becomes the seed label, the rest reasoning.
    const firstSentence = selectedText.split(/[.\n!?]/)[0].slice(0, 120) || selectedText.slice(0, 120);
    onDrillNovelty(firstSentence, selectedText, 'output selection');
  };
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const prevNodeCountRef = useRef(0);
  const [nodeFlash, setNodeFlash] = useState(false);

  // Flash effect when new nodes arrive during execution
  useEffect(() => {
    const count = displayNodes?.length || 0;
    if (isExecuting && count > prevNodeCountRef.current && prevNodeCountRef.current > 0) {
      setNodeFlash(true);
      const t = setTimeout(() => setNodeFlash(false), 600);
      return () => clearTimeout(t);
    }
    prevNodeCountRef.current = count;
  }, [displayNodes?.length, isExecuting]);

  // Build a flat ordered list of nodes for the rail
  const nodeList = useMemo(() => {
    if (!displayNodes?.length) return [];
    return displayNodes
      .filter(n => n.data?.label)
      .map(n => ({
        id: n.id,
        label: n.data.label,
        type: n.data.type,
        reasoning: n.data.reasoning,
        parentIds: n.data.parentIds,
      }));
  }, [displayNodes]);

  // Group nodes by type for the rail
  const groupedNodes = useMemo(() => {
    const groups = {};
    for (const node of nodeList) {
      const type = node.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(node);
    }
    return groups;
  }, [nodeList]);

  const selectedNode = nodeList.find(n => n.id === selectedNodeId);

  // Compute stage progress stats
  const stagesDone = stageHistory?.filter(s => s.status === 'done').length || 0;
  const stagesTotal = stageHistory?.length || 0;
  const activeStage = stageHistory?.find(s => s.status === 'active');

  return (
    <div className="wj-container">
      {/* ── Left Rail ── */}
      <div className="wj-rail">
        <div className="wj-rail-header">
          <div className="wj-rail-title">{patternName || 'Output'}</div>
          <div className="wj-rail-meta">
            <span className={nodeFlash ? 'wj-node-count-flash' : ''}>{nodeList.length} nodes</span>
            {Object.keys(groupedNodes).length > 0 && (
              <span> · {Object.keys(groupedNodes).length} types</span>
            )}
          </div>
        </div>

        {/* Progress section during execution */}
        {isExecuting && stageHistory?.length > 0 && (
          <div className="wj-progress-section">
            <div className="wj-progress-header">
              <span className="wj-progress-title">
                <span className="wj-exec-pulse">●</span> Running
              </span>
              {startedAt && <ElapsedTimer startedAt={startedAt} />}
            </div>
            <div className="wj-progress-bar-track">
              <div
                className="wj-progress-bar-fill"
                style={{ width: stagesTotal > 0 ? `${((stagesDone + 0.5) / stagesTotal) * 100}%` : '10%' }}
              />
            </div>
            <div className="wj-progress-stages">
              {stageHistory.map((stage, i) => (
                <StageItem key={`${stage.id}-${stage.round}-${i}`} stage={stage} />
              ))}
            </div>
          </div>
        )}

        <div className="wj-rail-stages">
          {/* "All Output" button */}
          <button
            className={`wj-stage-btn ${selectedNodeId === null ? 'selected' : ''}`}
            onClick={() => setSelectedNodeId(null)}
          >
            <span className="wj-node-icon" style={{ color: '#a78bfa' }}>◈</span>
            <div className="wj-stage-text">
              <span className="wj-stage-label">All Output</span>
              <span className="wj-stage-type">Full document</span>
            </div>
          </button>

          <div className="wj-rail-divider" />

          {/* Nodes grouped by type */}
          {Object.entries(groupedNodes).map(([type, nodes]) => {
            const config = getNodeConfig(type);
            return (
              <div key={type} className="wj-rail-group">
                <div className="wj-rail-group-header" style={{ color: config.color }}>
                  {config.icon} {config.label}
                  <span className="wj-rail-group-count">{nodes.length}</span>
                </div>
                {nodes.map(node => (
                  <button
                    key={node.id}
                    className={`wj-stage-btn wj-node-btn ${selectedNodeId === node.id ? 'selected' : ''}`}
                    onClick={() => setSelectedNodeId(node.id === selectedNodeId ? null : node.id)}
                  >
                    <span className="wj-node-dot" style={{ background: config.color }} />
                    <div className="wj-stage-text">
                      <span className="wj-stage-label">{node.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main Panel ── */}
      <div className="wj-main">
        {/* Toggle bar */}
        <div className="wj-main-toolbar">
          <div className="wj-main-toolbar-title">
            {selectedNode ? selectedNode.label : 'Output'}
          </div>
          <button className="wj-graph-toggle" onClick={onToggleGraphView}>
            <span className="wj-graph-toggle-icon">◇</span>
            Graph View
          </button>
        </div>

        {/* Content */}
        <div className="wj-main-content">
          {selectedNode ? (
            <div className="wj-node-detail">
              <div className="wj-stage-detail-meta">
                <TypeBadge type={selectedNode.type} />
              </div>
              <h2 className="wj-node-detail-title">{selectedNode.label}</h2>
              {selectedNode.reasoning && (
                <div className="wj-node-detail-reasoning">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedNode.reasoning}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          ) : synthesizedOutput ? (
            <div className="wj-output-doc">
              {contractVerification && <ContractChecklist {...contractVerification} />}
              {/* Doc toolbar: copy whole doc; deep-dive any selection.
                  Floats top-right so it doesn't push the markdown down. */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 4,
                  display: 'flex',
                  gap: 6,
                  justifyContent: 'flex-end',
                  background: 'linear-gradient(180deg, var(--bg-primary, #0a0a0f) 0%, var(--bg-primary, #0a0a0f) 70%, transparent 100%)',
                  padding: '6px 0 12px',
                  marginBottom: -8,
                  pointerEvents: 'none', // let buttons handle their own
                }}
              >
                {selectedText && onDrillNovelty && (
                  <button
                    type="button"
                    onClick={drillSelection}
                    aria-label={`Deep-dive into selected text (${selectedText.length} chars)`}
                    title="Drill into the selected idea using the novelty engine"
                    style={{
                      pointerEvents: 'auto',
                      background: 'var(--accent, #6c63ff)',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 10px',
                      fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    🔬 Deep-dive selection
                  </button>
                )}
                <button
                  type="button"
                  onClick={copyOutput}
                  aria-label={copyState === 'copied' ? 'Copied to clipboard' : 'Copy entire output to clipboard'}
                  title="Copy the full output document"
                  style={{
                    pointerEvents: 'auto',
                    background: copyState === 'copied' ? 'var(--success, #34d399)' : 'var(--bg-card, #1a1a24)',
                    color: copyState === 'copied' ? '#000' : 'var(--text-primary, #fff)',
                    border: '1px solid var(--border, #2a2a2a)',
                    padding: '6px 10px',
                    fontSize: 11,
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 600,
                    borderRadius: 4,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 76,
                    justifyContent: 'center',
                    transition: 'background 0.15s ease, color 0.15s ease',
                  }}
                >
                  {copyState === 'copied' ? '✓ Copied' : '⧉ Copy'}
                </button>
              </div>
              {isSynthesizing && (
                <div className="wj-synth-indicator">
                  <span className="wj-exec-pulse">●</span> Synthesizing deliverable...
                </div>
              )}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={onDrillNovelty ? {
                  // Per-section deep-dive: each h2/h3 gets a 🔬 button next to
                  // it that drills into the heading's text + the following
                  // paragraph. Lets users pivot off any specific bet without
                  // having to hand-select.
                  h2: ({ node, children, ...props }) => (
                    <h2 {...props} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>{children}</span>
                      <SectionActions heading={node} drill={onDrillNovelty} copyOne={copyOutput} />
                    </h2>
                  ),
                  h3: ({ node, children, ...props }) => (
                    <h3 {...props} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>{children}</span>
                      <SectionActions heading={node} drill={onDrillNovelty} copyOne={copyOutput} />
                    </h3>
                  ),
                } : undefined}
              >
                {synthesizedOutput}
              </ReactMarkdown>
            </div>
          ) : isExecuting ? (
            <div className="wj-executing-state">
              <div className="wj-exec-animation">
                <div className="wj-exec-ring" />
                <div className="wj-exec-ring wj-exec-ring-2" />
                <div className="wj-exec-ring wj-exec-ring-3" />
                <span className="wj-exec-icon">◈</span>
              </div>
              <div className="wj-exec-title">
                {patternName ? `Running ${patternName}` : 'Generating...'}
              </div>
              {activeStage && (
                <div className="wj-exec-stage">
                  <span className="wj-exec-pulse">●</span>
                  {activeStage.label}
                  {activeStage.detail && <span className="wj-exec-detail"> — {activeStage.detail}</span>}
                </div>
              )}
              {nodeList.length > 0 && (
                <div className="wj-exec-node-counter">
                  <span className={nodeFlash ? 'wj-node-count-flash' : ''}>{nodeList.length}</span> nodes generated
                </div>
              )}
              {startedAt && (
                <div className="wj-exec-elapsed">
                  <ElapsedTimer startedAt={startedAt} />
                </div>
              )}

              {/* Live node preview during execution */}
              {nodeList.length > 0 && (
                <div className="wj-exec-preview">
                  <div className="wj-exec-preview-title">Latest nodes:</div>
                  {nodeList.slice(-5).reverse().map(node => {
                    const config = getNodeConfig(node.type);
                    return (
                      <div key={node.id} className="wj-exec-preview-node">
                        <span className="wj-node-dot" style={{ background: config.color }} />
                        <span className="wj-exec-preview-label">{node.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="wj-empty">
              <div className="wj-empty-icon">◈</div>
              <div className="wj-empty-title">No output yet</div>
              <div className="wj-empty-sub">
                Run a workflow to see results here.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
